import { useRef } from 'react';
import styled from 'styled-components';
import { Swiper as SwiperType } from 'swiper';

import { IconButton } from '../../../../cdl/IconButton/IconButton';
import { IconChevronLeft } from '../../../../common/icons/cdl/ChevronLeft';
import { IconChevronRight } from '../../../../common/icons/cdl/ChevronRight';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { useScheduleProductColumns } from '../hooks/useScheduleProductColumns';
import { ScheduleLayout } from '../scheduleLayout';
import { isCurrentPort } from '../utils/isCurrentPort';

import { ScheduleBackgroundBars } from './ScheduleBackgroundBars';
import { SchedulePortColumn } from './SchedulePortColumn';
import { ScheduleItem } from './ScheduleProductCard';
import { ScheduleProductCategorySlider } from './ScheduleProductCategorySlider';

const NavigationButton = styled(IconButton)`
    &.swiper-button-disabled {
        color: ${theme.colors.foreground.disabled};
        background: transparent;
        pointer-events: none;
    }
`;

const NavigationContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: ${theme.space[8]};
    z-index: ${theme.zIndex[100]};
    background: ${theme.colors.background.default};
    padding-left: ${theme.space[16]};

    &::before {
        content: '';
        position: absolute;
        right: 100%;
        top: 0;
        bottom: 0;
        width: ${ScheduleLayout.PRODUCT_COLUMN_SPACER_END};
        background: linear-gradient(to right, transparent, ${theme.colors.background.default});
        pointer-events: none;
    }
`;

interface ScheduleProps {
    scheduleList: ScheduleItem[];
    showControls?: boolean;
}

export const Schedule = ({ scheduleList, showControls = true }: ScheduleProps) => {
    const swiperRef = useRef<SwiperType | null>(null);
    const prevButtonRef = useRef<HTMLButtonElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);

    const columns = useScheduleProductColumns(scheduleList);
    const firstStopIsCurrentStop = scheduleList.length > 0 && isCurrentPort(scheduleList[0].stop);

    return (
        <Box position="relative">
            {showControls ? (
                <NavigationContainer>
                    <NavigationButton Icon={IconChevronLeft} emphasis="medium" ref={prevButtonRef} />
                    <NavigationButton Icon={IconChevronRight} emphasis="medium" ref={nextButtonRef} />
                </NavigationContainer>
            ) : null}

            <ScheduleBackgroundBars scheduleList={scheduleList} />

            <Box display="flex" paddingLeft="40px">
                <SchedulePortColumn scheduleList={scheduleList} firstStopIsCurrentStop={firstStopIsCurrentStop} />
                <ScheduleProductCategorySlider
                    columns={columns}
                    prevButtonRef={prevButtonRef}
                    nextButtonRef={nextButtonRef}
                    onSwiperInit={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                />
            </Box>
        </Box>
    );
};
